import React, { useEffect, useState } from "react";
import { FlaskConical, Loader2, Trophy, AlertCircle } from "lucide-react";
import api from "@/lib/api";

/**
 * TrialABConversionTile — admin KPI tile for the 30d-vs-14d trial A/B test.
 *
 * Renders two side-by-side bucket cards (A control vs B treatment) with
 * enrolled / paid / lost counts + conversion %. Highlights the winner
 * once both buckets have ≥ MIN_SAMPLE_PER_BUCKET concluded trials; shows
 * a "sample too small" warning otherwise so admins don't call it early.
 *
 * Mounted on the Executive Dashboard, platform-admin only (endpoint
 * enforces that).
 */
export const TrialABConversionTile = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let active = true;
        api.get("/admin/trial-ab-conversion")
            .then((r) => { if (active) setData(r.data); })
            .catch((e) => { if (active) setError(e); })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, []);

    if (loading) {
        return (
            <div className="card-soft p-5" data-testid="trial-ab-tile-loading">
                <Loader2 size={16} className="animate-spin text-emerald-500" />
            </div>
        );
    }
    if (error || !data) return null; // Non-platform admins get 403 — just hide the tile.

    const a = data.buckets.A_30d;
    const b = data.buckets.B_14d;
    const winner = data.winner;
    const warn = data.sample_size_warning;
    // iter-159 — Per-source channel breakdown (sorted highest conversion
    // first by the backend). Only renders if there's at least one
    // source row with enrollment, otherwise we hide the strip entirely.
    const sources = Array.isArray(data.sources)
        ? data.sources.filter((s) => s.enrolled > 0)
        : [];

    const BucketCard = ({ bucket, isWinner }) => (
        <div
            className={`rounded-lg border p-4 flex-1 min-w-0 ${isWinner ? "border-emerald-500/60 bg-emerald-500/5" : "border-border bg-card"}`}
            data-testid={`trial-ab-bucket-${bucket.trial_days}d`}
        >
            <div className="flex items-center justify-between gap-2 mb-2">
                <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{bucket.label}</span>
                {isWinner && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500 text-white px-2 py-0.5 text-[10px] font-bold" data-testid={`trial-ab-winner-${bucket.trial_days}d`}>
                        <Trophy size={9} /> Winner
                    </span>
                )}
            </div>
            <div className="flex items-baseline gap-1">
                <span className="font-heading font-bold text-3xl text-foreground">{bucket.conversion_pct}%</span>
                <span className="text-xs text-muted-foreground">conversion</span>
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
                <div>
                    <div className="font-semibold text-foreground">{bucket.paid}</div>
                    <div className="text-[10px] text-muted-foreground uppercase tracking-wider">Paid</div>
                </div>
                <div>
                    <div className="font-semibold text-foreground">{bucket.trialing}</div>
                    <div className="text-[10px] text-muted-foreground uppercase tracking-wider">Trialing</div>
                </div>
                <div>
                    <div className="font-semibold text-foreground">{bucket.lost}</div>
                    <div className="text-[10px] text-muted-foreground uppercase tracking-wider">Lost</div>
                </div>
            </div>
            <div className="mt-3 pt-2 border-t border-border/60 text-[10px] text-muted-foreground">
                {bucket.enrolled} enrolled · {bucket.concluded} concluded
            </div>
        </div>
    );

    return (
        <div className="card-soft p-5" data-testid="trial-ab-conversion-tile">
            <div className="flex items-center gap-2 mb-4">
                <div className="h-9 w-9 rounded-xl bg-violet-500/10 text-violet-600 flex items-center justify-center">
                    <FlaskConical size={16} />
                </div>
                <div className="min-w-0">
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Trial length A/B</div>
                    <div className="text-sm font-heading font-semibold text-foreground">30-day vs 14-day conversion</div>
                </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
                <BucketCard bucket={a} isWinner={winner === "A_30d"} />
                <BucketCard bucket={b} isWinner={winner === "B_14d"} />
            </div>
            {warn && (
                <div className="mt-3 flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/5 p-2 text-[11px] text-amber-800 dark:text-amber-300" data-testid="trial-ab-sample-warning">
                    <AlertCircle size={12} className="mt-0.5 shrink-0" />
                    <span>
                        Sample too small to call. Need at least {data.min_sample_per_bucket} concluded trials in each bucket before the winner can be trusted.
                    </span>
                </div>
            )}
            {!warn && !winner && (
                <div className="mt-3 text-[11px] text-muted-foreground" data-testid="trial-ab-tied">
                    Buckets are tied — keep running the test.
                </div>
            )}
            {sources.length > 0 && (
                <div className="mt-5 pt-4 border-t border-border" data-testid="trial-ab-sources">
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                        Conversion by source
                    </div>
                    <ul className="space-y-1.5">
                        {sources.map((s) => (
                            <li
                                key={s.source}
                                className="grid grid-cols-[1fr_auto_auto] items-center gap-3 text-xs"
                                data-testid={`trial-ab-source-${s.source}`}
                            >
                                <span className="font-mono text-[11px] text-foreground truncate">{s.source}</span>
                                <span className="text-muted-foreground tabular-nums">
                                    {s.paid}/{s.concluded}
                                </span>
                                <span className={`font-semibold tabular-nums ${
                                    s.conversion_pct >= (a.conversion_pct + b.conversion_pct) / 2
                                        ? "text-emerald-600" : "text-muted-foreground"
                                }`}>
                                    {s.conversion_pct}%
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default TrialABConversionTile;
